import { useCallback, useEffect, useState } from "react";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type SeoRow = {
  key: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
};

export type SeoReport = {
  status: "ok";
  siteUrl: string;
  targetUrl: string;
  period: { startDate: string; endDate: string; days: number };
  totals: { clicks?: number; impressions?: number; ctr?: number; position?: number } | null;
  timeseries: Array<{ date: string; clicks: number; impressions: number; ctr: number; position: number }>;
  topQueries: SeoRow[];
  topPages: SeoRow[];
  indexing: {
    verdict: string | null;
    coverageState: string | null;
    robotsTxtState: string | null;
    indexingState: string | null;
    googleCanonical: string | null;
    userCanonical: string | null;
    lastCrawlTime: string | null;
    pageFetchState: string | null;
  } | null;
  sitemaps: Array<{
    path: string;
    lastSubmitted: string | null;
    lastDownloaded: string | null;
    isPending: boolean;
    errors: number;
    warnings: number;
  }>;
};

type State = {
  status: "idle" | "loading" | "ready" | "error" | "selection_required";
  report: SeoReport | null;
  candidates: string[];
  error: string | null;
};

export function useSeoMonitoring(days = 28) {
  const [state, setState] = useState<State>({
    status: "idle",
    report: null,
    candidates: [],
    error: null,
  });

  const load = useCallback(
    async (selectedSiteUrl?: string, requestedDays = days) => {
      setState((prev) => ({ ...prev, status: "loading", error: null }));
      const { data, error } = await supabase.functions.invoke("seo-monitoring", {
        body: { days: requestedDays, ...(selectedSiteUrl ? { selectedSiteUrl } : {}) },
      });

      if (error) {
        let details = error.message;
        let parsed: { status?: string; candidates?: string[]; error?: string } | null = null;
        if (error instanceof FunctionsHttpError) {
          details = await error.context.text();
          try {
            parsed = JSON.parse(details);
          } catch {
            parsed = null;
          }
        }
        if (parsed?.status === "selection_required" && parsed.candidates?.length) {
          setState({
            status: "selection_required",
            report: null,
            candidates: parsed.candidates,
            error: null,
          });
          return;
        }
        console.error("seo-monitoring failed:", details);
        setState({
          status: "error",
          report: null,
          candidates: [],
          error: parsed?.error ?? details ?? "Could not load Search Console data",
        });
        return;
      }

      setState({ status: "ready", report: data as SeoReport, candidates: [], error: null });
    },
    [days],
  );

  useEffect(() => {
    void load();
  }, [load]);

  return { ...state, reload: load };
}
